/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { ListOption, ListProps, SelectType } from '../protocols';
import { Option } from '../styles';

export type ListPagePayload = {
  message?: string;
  count: string | number;
  data: ListOption[];
  fields?: any[];
  pagination?: any;
  success?: boolean;

  // Compat e agrupamento
  loja?: number; // quando for consulta unitária (compat)
  grouped?: boolean; // true quando houver mais de uma loja
  lojas?: number[]; // ids consultados
  groupMap?: Record<number, ListOption[]>; // opções por loja
};

export interface ListData {
  pages: ListPagePayload[];
}

export const Lista: React.FC<ListProps> = ({
  inputRef,
  setIsOpen,
  setFilter,
  hasNextPage,
  fetchNextPage,
  data,
  selected,
  setSelected,
  isFetching,
  changeSelected,
  queryName,
  queryClient,
}) => {
  const wrapperRef = useRef<HTMLUListElement | null>(null);

  /** Fecha lista ao clicar fora */
  function useCloseOptions(ref: React.RefObject<HTMLUListElement>) {
    useEffect(() => {
      function handleClickOutside(event: any) {
        if (ref.current && !ref.current.contains(event.target)) {
          if (inputRef?.current) inputRef.current.value = '';
          setFilter('');
          queryClient.removeQueries(queryName);
          setIsOpen(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref]);
  }
  useCloseOptions(wrapperRef);

  /** Scroll infinito */
  const onScroll = async () => {
    if (wrapperRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = wrapperRef.current;
      const zoomLevel = Math.round(window.devicePixelRatio * 100);
      const offset = Math.abs(100 - zoomLevel);
      if (scrollTop + clientHeight >= scrollHeight - offset) {
        if (hasNextPage) {
          await fetchNextPage();
        }
      }
    }
  };

  /** Seleciona item */
  const handleClick = useCallback(
    (option: ListOption) => {
      const normalized: SelectType = {
        value: option.value,
        label: option.label,
        lojaId: option.lojaId,
      };
      setSelected(option);
      changeSelected(normalized);
      setIsOpen(false);
    },
    [changeSelected, setIsOpen, setSelected],
  );

  /** Renderização de grupos por loja (quando houver mais de uma loja) */
  const renderGroupedByLoja = (page: ListPagePayload) => {
    if (!page.lojas?.length || !page.groupMap) return null;

    const multiple = (page.lojas?.length ?? 0) > 1;

    return page.lojas.map((lj) => {
      const group = page.groupMap?.[lj] ?? [];
      return (
        <React.Fragment key={`group-${lj}`}>
          {multiple && (
            <Option
              className="group-title"
              as="li"
              aria-label={`Grupo da Loja ${lj}`}
              role="presentation"
              style={{
                fontWeight: 600,
                padding: '6px 10px',
                cursor: 'default',
                userSelect: 'none',
                opacity: 0.8,
              }}
            >
              Loja {lj}
            </Option>
          )}
          {group.length === 0 && (
            <Option className="not-found">Nenhum registro encontrado.</Option>
          )}
          {group.map((item) => (
            <Option
              key={`${lj}-${item.value}`}
              className={selected.value === item.value ? 'selected' : ''}
              onClick={() => {
                handleClick(item);
                if (inputRef?.current) {
                  inputRef.current.placeholder = `${item.label}`;
                  inputRef.current.value = '';
                }
                queryClient.removeQueries(queryName);
                setFilter('');
              }}
              onKeyPress={() => {
                handleClick(item);
                if (inputRef?.current) {
                  inputRef.current.placeholder = `${item.label}`;
                  inputRef.current.value = '';
                }
                queryClient.removeQueries(queryName);
                setFilter('');
              }}
            >
              {`${item.label}`}
            </Option>
          ))}
        </React.Fragment>
      );
    });
  };

  /** Render genérico */
  const renderOptions = useCallback(() => {
    if (!data) {
      return <Option className="not-found">Nenhum registro encontrado.</Option>;
    }

    const first = data.pages[0];

    if (first?.message === 'url not found') {
      return <Option className="not-found">Nenhum registro encontrado.</Option>;
    }

    // Quando vier agrupado por lojas (multi)
    return data.pages.map((page, idx) => {
      // Caso agrupado com mais de uma loja -> títulos "Loja X"
      if (page.grouped && (page.lojas?.length ?? 0) > 1 && page.groupMap) {
        return (
          <React.Fragment key={`page-grouped-${idx}`}>
            {renderGroupedByLoja(page)}
          </React.Fragment>
        );
      }

      // Caso unitário (uma loja só ou não-array)
      if (!page.data?.length) {
        return (
          <Option className="not-found" key={`page-empty-${idx}`}>
            Nenhum registro encontrado.
          </Option>
        );
      }

      return page.data.map((item: any) => (
        <Option
          key={item.value}
          className={selected.value === item.value ? 'selected' : ''}
          onClick={() => {
            handleClick(item);
            if (inputRef?.current) {
              inputRef.current.placeholder = `${item.label}`;
              inputRef.current.value = '';
            }
            queryClient.removeQueries(queryName);
            setFilter('');
          }}
          onKeyPress={() => {
            handleClick(item);
            if (inputRef?.current) {
              inputRef.current.placeholder = `${item.label}`;
              inputRef.current.value = '';
            }
            queryClient.removeQueries(queryName);
            setFilter('');
          }}
        >
          {`${item.label}`}
        </Option>
      ));
    });
  }, [
    data,
    handleClick,
    inputRef,
    queryClient,
    queryName,
    selected.value,
    setFilter,
  ]);

  return (
    <ul ref={wrapperRef} onScroll={onScroll}>
      {data
        ? renderOptions()
        : !isFetching && (
            <Option className="not-found">
              Digite ao menos 3 caracteres para pesquisar.
            </Option>
          )}
      {isFetching && (
        <Option className="not-found">
          <Spinner animation="border" size="sm" className="spinner" />
        </Option>
      )}
    </ul>
  );
};
